/* eslint-disable max-lines -- legacy size; refactor planned in plans/cached-singing-fiddle.md */
import {
  useEditor,
  EditorContent,
  type Editor,
  Extension,
} from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import LinkExtension from "@tiptap/extension-link";
import Mention from "@tiptap/extension-mention";
import Placeholder from "@tiptap/extension-placeholder";
import {
  forwardRef,
  memo,
  useImperativeHandle,
  useEffect,
  useRef,
} from "react";
import { MarkdownExtension, getEditorMarkdown } from "@/lib/editor-markdown";
import {
  getFilesFromClipboard,
  validatePastedFiles,
  showPasteErrors,
} from "@/lib/clipboard-utils";
import { detectCodeFromClipboard } from "@/lib/paste-code-detection";
import { Plugin, PluginKey, Selection } from "@tiptap/pm/state";
import { canJoin } from "@tiptap/pm/transform";
import { usePropDiffLog } from "@/lib/use-prop-diff-log";
import { markInput } from "@/lib/perf/input-latency";

export interface TiptapEditorRef {
  focus: () => void;
  clear: () => void;
  setContentFromMarkdown: (md: string) => void;
  getMarkdown: () => string;
  getText: () => string;
  isEmpty: () => boolean;
  insertText: (text: string) => void;
  getEditor: () => Editor | null;
}

interface TiptapEditorProps {
  placeholder?: string;
  disabled?: boolean;
  onSend?: () => void;
  onBroadcastSend?: () => void;
  onChange?: () => void;
  onBlur?: () => void;
  /** Called when files are pasted (images, documents) */
  onFilePaste?: (files: File[]) => void;
  /** Current count of pending attachments (for validation) */
  pendingAttachmentCount?: number;
  /** Whether mention popup is open (blocks Enter-to-send) */
  mentionPopupOpen?: boolean;
  /** Whether slash command popup is open (blocks Enter-to-send) */
  slashPopupOpen?: boolean;
  /** Whether emoji shortcode popup is open (blocks Enter-to-send) */
  emojiPopupOpen?: boolean;
  /** Called when Escape is pressed */
  onEscape?: () => void;
  /** Compact mode for inline editing (no min-height) */
  compact?: boolean;
  /** Auto-focus on mount */
  autoFocus?: boolean;
}

const FilePastePlugin = (
  onFilePaste?: (files: File[]) => void,
  getPendingCount?: () => number,
) =>
  new Plugin({
    key: new PluginKey("filePaste"),
    props: {
      handlePaste(_view, event) {
        if (!onFilePaste || !event.clipboardData) return false;

        const files = getFilesFromClipboard(event.clipboardData);
        if (files.length === 0) return false;

        // Files found — handle them and prevent default paste
        event.preventDefault();
        const { valid, errors } = validatePastedFiles(
          files,
          getPendingCount?.() ?? 0,
        );
        if (valid.length > 0) onFilePaste(valid);
        showPasteErrors(errors);
        return true;
      },
    },
  });

// Memo'd so that ancestor renders (which happen often — see e.g. the
// `MessageInput` "INTERNAL (no prop change)" log entries during
// presence heartbeats) don't pull TiptapEditor's render through. The
// underlying ProseMirror tree is owned by `useEditor([])` and survives
// memo-skipped renders cleanly. Re-renders only fire when one of the
// scalar props (placeholder, disabled, popover-open booleans, autoFocus,
// pendingAttachmentCount) flips — or when the parent passes a new
// callback identity, which we minimize by `useCallback`-ing
// `onBroadcastSend` in `MessageInput`.
export const TiptapEditor = memo(
  forwardRef<TiptapEditorRef, TiptapEditorProps>(function TiptapEditor(
    {
      placeholder = "Type a message...",
      disabled = false,
      onSend,
      onBroadcastSend,
      onChange,
      onBlur,
      onFilePaste,
      pendingAttachmentCount = 0,
      mentionPopupOpen = false,
      slashPopupOpen = false,
      emojiPopupOpen = false,
      onEscape,
      compact = false,
      autoFocus = false,
    },
    ref,
  ) {
    // ─── Render diagnostics ─────────────────────────────────────────
    // Prop-diff log so we can see which prop reference flipped if the
    // memo busts. Every prop on the interface is included so a parent
    // passing a fresh callback identity (e.g. an inline arrow on
    // `onSend` / `onBroadcastSend`) shows up by name in `CHANGED:`.
    usePropDiffLog(
      "TiptapEditor",
      {
        placeholder,
        disabled,
        onSend,
        onBroadcastSend,
        onChange,
        onBlur,
        onFilePaste,
        pendingAttachmentCount,
        mentionPopupOpen,
        slashPopupOpen,
        emojiPopupOpen,
        onEscape,
        compact,
        autoFocus,
      },
      `popups=m${mentionPopupOpen ? 1 : 0}/s${slashPopupOpen ? 1 : 0}/e${emojiPopupOpen ? 1 : 0} disabled=${disabled}`,
    );

    // Mutable refs so the ProseMirror keymap always sees latest values
    // without re-creating the editor (which loses cursor position).
    const mentionRef = useRef(mentionPopupOpen);
    mentionRef.current = mentionPopupOpen;
    const slashRef = useRef(slashPopupOpen);
    slashRef.current = slashPopupOpen;
    const emojiRef = useRef(emojiPopupOpen);
    emojiRef.current = emojiPopupOpen;
    const onSendRef = useRef(onSend);
    onSendRef.current = onSend;
    const onBroadcastRef = useRef(onBroadcastSend);
    onBroadcastRef.current = onBroadcastSend;
    const onEscapeRef = useRef(onEscape);
    onEscapeRef.current = onEscape;
    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;
    const onBlurRef = useRef(onBlur);
    onBlurRef.current = onBlur;
    const onFilePasteRef = useRef(onFilePaste);
    onFilePasteRef.current = onFilePaste;
    const pendingCountRef = useRef(pendingAttachmentCount);
    pendingCountRef.current = pendingAttachmentCount;

    const editor = useEditor(
      {
        extensions: [
          StarterKit.configure({
            heading: false,
            horizontalRule: false,
            dropcursor: false,
            gapcursor: false,
            paragraph: {
              HTMLAttributes: {
                style: "margin: 0;",
              },
            },
            code: {
              HTMLAttributes: {
                style:
                  "font-family: var(--app-font-mono); font-size: 0.75rem; color: hsl(206 100% 50%); background: hsl(0 0% 16% / 0.5); border-radius: 6px; padding: 0.125rem 0.25rem;",
              },
            },
            bulletList: {
              HTMLAttributes: {
                style:
                  "list-style-type: disc; padding-left: 1.5em; margin: 0.25em 0;",
              },
            },
            orderedList: {
              HTMLAttributes: {
                style:
                  "list-style-type: decimal; padding-left: 1.5em; margin: 0.25em 0;",
              },
            },
            blockquote: {
              HTMLAttributes: {
                // Use semantic tokens so blockquotes read on either theme
                // (border tracks --border, text tracks the muted hierarchy).
                style:
                  "border-left: 3px solid hsl(var(--border)); padding-left: 0.75em; margin: 0.25em 0; color: hsl(var(--muted-foreground));",
              },
            },
          }),
          LinkExtension.configure({
            openOnClick: false,
            autolink: true,
            linkOnPaste: true,
          }),
          Placeholder.configure({ placeholder }),
          MarkdownExtension,
          Mention.configure({
            HTMLAttributes: {
              // `font-medium` (Tailwind) replaces the previous inline
              // `font-weight: 500;` style — that inline weight matched
              // Tiptap's Bold extension `parseDOM` rule
              // (`/^(bold(er)?|[5-9]\d{2,})$/.test(value)`), so any
              // serializer pass that re-parsed DOM marked the mention as
              // bold and tiptap-markdown wrote `**@name**` to the wire
              // (#1776). The class-based weight is invisible to the
              // parseDOM style sniff.
              class: "mention font-medium",
              // Use the brand/primary token so @mentions stay in the theme
              // family — yellow (#fde047) was matcha2-only and clashed with
              // the linen light theme.
              style: "color: hsl(var(--primary));",
            },
            deleteTriggerWithBackspace: true,
            renderText({ node }) {
              return `@${node.attrs.label ?? node.attrs.id}`;
            },
          }).extend({
            addStorage() {
              return {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                ...(this as any).parent?.(),
                markdown: {
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  serialize(state: any, node: any) {
                    state.write(`@${node.attrs.label ?? node.attrs.id}`);
                  },
                  parse: {},
                },
              };
            },
            // Override the buggy upstream Backspace handler that
            // double-processes mention deletion (two nodesBetween
            // loops modifying the same transaction).
            addKeyboardShortcuts() {
              return {
                Backspace: () =>
                  this.editor.commands.command(({ tr, state }) => {
                    const { selection } = state;
                    if (!selection.empty) return false;
                    const { anchor } = selection;
                    let found = false;
                    state.doc.nodesBetween(anchor - 1, anchor, (node, pos) => {
                      if (node.type.name === this.name) {
                        found = true;
                        const replacement = this.options
                          .deleteTriggerWithBackspace
                          ? ""
                          : node.attrs.mentionSuggestionChar || "@";
                        tr.insertText(replacement, pos, pos + node.nodeSize);
                        return false;
                      }
                    });
                    return found;
                  }),
              };
            },
          }),
          // Enter-to-send keymap
          Extension.create({
            name: "enterToSend",
            addKeyboardShortcuts() {
              return {
                Backspace: ({ editor: ed }) => {
                  const { selection } = ed.state;
                  // Selection deletion already works natively
                  if (!selection.empty) return false;
                  const { $from } = selection;
                  // At start of first list item → lift out of list
                  if (
                    $from.parentOffset === 0 &&
                    ed.isActive("listItem") &&
                    $from.index($from.depth - 1) === 0
                  ) {
                    return ed.commands.liftListItem("listItem");
                  }
                  // At start of non-first list item → merge with previous item
                  if ($from.parentOffset === 0 && ed.isActive("listItem")) {
                    const beforeLi = $from.before($from.depth - 1);
                    const { tr } = ed.state;
                    if (canJoin(tr.doc, beforeLi)) {
                      tr.join(beforeLi);
                      const mapped = tr.mapping.map(selection.from);
                      if (canJoin(tr.doc, mapped)) tr.join(mapped);
                      ed.view.dispatch(tr);
                      return true;
                    }
                    return false;
                  }
                  // At start of blockquote → lift out
                  if ($from.parentOffset === 0 && ed.isActive("blockquote")) {
                    return ed.commands.lift("blockquote");
                  }
                  // At start of text block
                  if ($from.parentOffset === 0) {
                    // If previous sibling is a list, don't fall through
                    // to joinBackward (it would auto-wrap this paragraph
                    // back into the list)
                    const prevNode = $from
                      .node($from.depth - 1)
                      .maybeChild($from.index($from.depth - 1) - 1);
                    if (
                      prevNode &&
                      (prevNode.type.name === "bulletList" ||
                        prevNode.type.name === "orderedList")
                    ) {
                      if ($from.parent.content.size === 0) {
                        // Empty paragraph — delete it and place cursor
                        // at end of the last item in the previous list
                        const from = $from.before($from.depth);
                        const to = $from.after($from.depth);
                        const tr = ed.state.tr.delete(from, to);
                        const resolvedEnd = tr.doc.resolve(from);
                        tr.setSelection(Selection.near(resolvedEnd, -1));
                        ed.view.dispatch(tr);
                        return true;
                      }
                      // Non-empty — join at the block boundary
                      const pos = $from.before($from.depth);
                      const { tr } = ed.state;
                      if (canJoin(tr.doc, pos)) {
                        tr.join(pos);
                        ed.view.dispatch(tr);
                        return true;
                      }
                    }
                    return false;
                  }
                  // No custom path needed — let ProseMirror's default
                  // backspace handle the within-block "delete one char"
                  // case. The earlier ed.view.dispatch(tr.delete(...))
                  // shortcut existed only because the (now-removed)
                  // FakeCaretExtension's widget decoration was breaking
                  // native contenteditable deletion + OS dead-key
                  // composition.
                  return false;
                },
                Enter: () => {
                  // When popup is open, consume the event to prevent
                  // splitBlock — the document keydown listener handles
                  // the actual selection.
                  if (
                    mentionRef.current ||
                    slashRef.current ||
                    emojiRef.current
                  )
                    return true;
                  onSendRef.current?.();
                  return true;
                },
                "Shift-Enter": ({ editor: ed }) => {
                  // In a list → new list item
                  if (ed.isActive("listItem")) {
                    return ed.commands.splitListItem("listItem");
                  }
                  // splitBlock for all cases — creates separate <p> nodes
                  // so block formatting (lists, quotes) scopes to individual lines
                  return ed.commands.splitBlock();
                },
                "Mod-Enter": () => {
                  if (
                    mentionRef.current ||
                    slashRef.current ||
                    emojiRef.current
                  )
                    return false;
                  onBroadcastRef.current?.();
                  return true;
                },
                Escape: () => {
                  if (onEscapeRef.current) {
                    onEscapeRef.current();
                    return true;
                  }
                  return false;
                },
              };
            },
          }),
          // File paste handling
          Extension.create({
            name: "filePaste",
            addProseMirrorPlugins() {
              return [
                FilePastePlugin(
                  (...args) => onFilePasteRef.current?.(...args),
                  () => pendingCountRef.current,
                ),
              ];
            },
          }),
        ],
        editable: !disabled,
        onUpdate: () => {
          markInput("composer:input");
          onChangeRef.current?.();
        },
        onBlur: () => {
          onBlurRef.current?.();
        },
        editorProps: {
          attributes: {
            class: compact
              ? "tiptap tiptap-compact outline-none text-sm text-foreground"
              : "tiptap outline-none text-sm leading-5 text-foreground min-h-[45px] max-h-32 overflow-y-auto",
          },
          // Auto-detect when the user pastes code from an IDE / docs
          // site / source viewer and wrap the paste in a codeBlock node
          // so it sends as a fenced block. Conservative: only triggers
          // on a "dominant <pre>" in the clipboard's text/html — i.e.
          // when the source app intentionally marked it as code. Plain
          // prose pastes still use Tiptap's default handler.
          handlePaste: (view, event) => {
            const detected = detectCodeFromClipboard(event);
            if (!detected) return false;
            const { text, language } = detected;
            const codeBlockType = view.state.schema.nodes.codeBlock;
            if (!codeBlockType) return false;
            const node = codeBlockType.create(
              language ? { language } : undefined,
              text ? view.state.schema.text(text) : null,
            );
            const tr = view.state.tr.replaceSelectionWith(node);
            view.dispatch(tr.scrollIntoView());
            event.preventDefault();
            return true;
          },
        },
        immediatelyRender: true,
      },
      [],
    );

    // Toggle editable when disabled changes
    useEffect(() => {
      if (editor && editor.isEditable === disabled) {
        editor.setEditable(!disabled);
      }
    }, [editor, disabled]);

    // Auto-focus
    useEffect(() => {
      if (autoFocus && editor) {
        // Small delay to ensure DOM is ready
        requestAnimationFrame(() => editor.commands.focus());
      }
    }, [autoFocus, editor]);

    useImperativeHandle(
      ref,
      () => ({
        focus: () => editor?.commands.focus(),
        clear: () => editor?.commands.clearContent(true),
        setContentFromMarkdown: (md: string) => {
          if (!editor) return;
          editor.commands.setContent(md);
        },
        getMarkdown: () => {
          if (!editor) return "";
          return getEditorMarkdown(editor);
        },
        getText: () => editor?.getText() ?? "",
        isEmpty: () => editor?.isEmpty ?? true,
        insertText: (text: string) => {
          editor?.commands.insertContent(text);
        },
        getEditor: () => editor,
      }),
      [editor],
    );

    return <EditorContent editor={editor} />;
  }),
);
